import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  User, Mail, Phone, Building2, MapPin, Briefcase, 
  Linkedin, Twitter, Globe, Calendar, TrendingUp, 
  ExternalLink, Copy, CheckCircle, Clock
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface LeadMetadata {
  harvested_from?: string;
  source_type?: string;
  job_title?: string;
  department?: string;
  seniority_level?: string;
  linkedin_url?: string;
  twitter_handle?: string;
  company_linkedin?: string;
  company_industry?: string;
  company_size?: string;
  harvested_at?: string;
  [key: string]: any;
}

interface Lead {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  company_name: string | null;
  location: string | null;
  source: string | null;
  status: string | null;
  lead_score: number | null;
  ai_conversion_score: number | null;
  created_at: string;
  last_activity_at: string | null;
  tags: string[] | null;
  metadata: LeadMetadata | null;
}

interface LeadDetailSheetProps {
  lead: Lead | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const statusConfig: Record<string, { label: string; className: string }> = {
  new: { label: 'New', className: 'bg-blue-500/20 text-blue-400' },
  attempted_contact: { label: 'Attempted', className: 'bg-yellow-500/20 text-yellow-400' },
  engaged: { label: 'Engaged', className: 'bg-purple-500/20 text-purple-400' },
  qualified: { label: 'Qualified', className: 'bg-green-500/20 text-green-400' },
  unqualified: { label: 'Unqualified', className: 'bg-red-500/20 text-red-400' },
  converted: { label: 'Converted', className: 'bg-primary/20 text-primary' },
};

const seniorityLabels: Record<string, string> = {
  'entry': 'Entry Level',
  'mid': 'Mid Level',
  'senior': 'Senior',
  'executive': 'Executive',
  'c-level': 'C-Level',
};

export function LeadDetailSheet({ lead, open, onOpenChange }: LeadDetailSheetProps) {
  if (!lead) return null;

  const metadata = lead.metadata || {};
  const status = statusConfig[lead.status || 'new'] || statusConfig.new;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const ScoreIndicator = ({ score, label }: { score: number; label: string }) => (
    <div className="flex items-center gap-2">
      <div className="flex-1">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-muted-foreground">{label}</span>
          <span className="font-semibold">{score}</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all"
            style={{ 
              width: `${score}%`,
              background: score >= 70 ? 'hsl(var(--primary))' : score >= 40 ? 'hsl(var(--warning))' : 'hsl(var(--destructive))'
            }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
              <User className="h-6 w-6 text-primary" />
            </div>
            <div>
              <div className="text-lg font-semibold">
                {lead.first_name} {lead.last_name}
              </div>
              {metadata.job_title && (
                <div className="text-sm text-muted-foreground">{metadata.job_title}</div>
              )}
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Status & Scores */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={status.className}>{status.label}</Badge>
            {metadata.seniority_level && (
              <Badge variant="outline">{seniorityLabels[metadata.seniority_level] || metadata.seniority_level}</Badge>
            )}
            {metadata.department && (
              <Badge variant="secondary">{metadata.department}</Badge>
            )}
          </div>

          {/* Scores */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Lead Scores
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {lead.lead_score !== null && (
                <ScoreIndicator score={lead.lead_score} label="Lead Score" />
              )}
              {lead.ai_conversion_score !== null && (
                <ScoreIndicator score={lead.ai_conversion_score} label="AI Conversion Score" />
              )}
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="h-4 w-4" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {lead.email && (
                <div className="flex items-center justify-between group">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a href={`mailto:${lead.email}`} className="hover:text-primary">{lead.email}</a>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 opacity-0 group-hover:opacity-100"
                    onClick={() => copyToClipboard(lead.email!, 'Email')}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              )}
              {lead.phone && (
                <div className="flex items-center justify-between group">
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a href={`tel:${lead.phone}`} className="hover:text-primary">{lead.phone}</a>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 opacity-0 group-hover:opacity-100"
                    onClick={() => copyToClipboard(lead.phone!, 'Phone')}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              )}
              {metadata.linkedin_url && (
                <div className="flex items-center gap-2 text-sm">
                  <Linkedin className="h-4 w-4 text-muted-foreground" />
                  <a 
                    href={metadata.linkedin_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-primary flex items-center gap-1"
                  >
                    LinkedIn Profile
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              {metadata.twitter_handle && (
                <div className="flex items-center gap-2 text-sm">
                  <Twitter className="h-4 w-4 text-muted-foreground" />
                  <a 
                    href={`https://twitter.com/${metadata.twitter_handle.replace('@', '')}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-primary flex items-center gap-1"
                  >
                    {metadata.twitter_handle}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Company Information */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Company Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {lead.company_name && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{lead.company_name}</span>
                </div>
              )}
              {lead.location && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{lead.location}</span>
                </div>
              )}
              {metadata.company_industry && (
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="h-4 w-4 text-muted-foreground" />
                  <Badge variant="outline">{metadata.company_industry}</Badge>
                </div>
              )}
              {metadata.company_size && (
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>{metadata.company_size} employees</span>
                </div>
              )}
              {metadata.company_linkedin && (
                <div className="flex items-center gap-2 text-sm">
                  <Linkedin className="h-4 w-4 text-muted-foreground" />
                  <a 
                    href={metadata.company_linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-primary flex items-center gap-1"
                  >
                    Company LinkedIn
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Source & Harvesting Info */}
          {metadata.harvested_from && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Harvesting Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <a 
                    href={metadata.harvested_from} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-primary flex items-center gap-1 truncate max-w-[280px]"
                  >
                    {metadata.harvested_from}
                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  </a>
                </div>
                {metadata.source_type && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-muted-foreground" />
                    <span>Source: <Badge variant="secondary">{metadata.source_type}</Badge></span>
                  </div>
                )}
                {metadata.harvested_at && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>Harvested: {format(new Date(metadata.harvested_at), 'PPp')}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tags */}
          {lead.tags && lead.tags.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Tags</h4>
              <div className="flex flex-wrap gap-1">
                {lead.tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <Separator />

          {/* Timestamps */}
          <div className="text-xs text-muted-foreground space-y-1">
            <div className="flex items-center gap-2">
              <Calendar className="h-3 w-3" />
              Created: {format(new Date(lead.created_at), 'PPp')}
            </div>
            {lead.last_activity_at && (
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3" />
                Last Activity: {format(new Date(lead.last_activity_at), 'PPp')}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
